import { z } from "zod";
import { ContactChannelSchema } from "./contact-channels.js";
export const NativeCalendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).refine((value) => value >= "2020-01-01" && value <= "2200-12-31" && Number.isFinite(Date.parse(`${value}T00:00:00Z`))
    && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value, "Дата не существует.");
export const NativeCalendarHourSchema = z.number().int().min(6).max(22);
export const NativeCalendarIdempotencyKeySchema = z.string().min(16).max(160);
export const NativeCalendarRoleSchema = z.enum(["leader", "manager", "caller"]);
// active:false only in a staff snapshot: a consultant who left the team list
// but still holds booked appointments in the period (they must be moved).
export const NativeCalendarManagerSchema = z.object({ id: z.string().uuid(), name: z.string().min(1).max(200), active: z.boolean().optional() }).strict();
export const NativeCalendarProfileSchema = z.object({
    role: NativeCalendarRoleSchema,
    managerId: z.string().uuid().nullable(),
}).strict();
export const NativeCalendarIntegrationModeSchema = z.enum(["planned_not_applied", "connected"]);
export const NativeCalendarBootstrapSchema = z.object({
    enabled: z.literal(true), timezone: z.literal("Europe/Moscow"), now: z.string().datetime(),
    startHour: z.literal(6), endHour: z.literal(22), slotMinutes: z.literal(60),
    minimumLeadMinutes: z.literal(180), integrationMode: NativeCalendarIntegrationModeSchema,
    profile: NativeCalendarProfileSchema, operatorAccess: z.boolean(),
    managers: z.array(NativeCalendarManagerSchema), leadHost: z.string().min(1).max(253),
}).strict();
export const NativeCalendarRangeSchema = z.object({ from: NativeCalendarDateSchema, to: NativeCalendarDateSchema }).strict().refine(({ from, to }) => to >= from && Date.parse(to) - Date.parse(from) <= 61 * 86_400_000, "Выберите период от одного до 62 дней.");
export const NativeCalendarBookingSchema = z.object({
    id: z.string().uuid(), managerId: z.string().uuid(), date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema,
    state: z.enum(["booked", "cancelled", "elapsed"]), version: z.number().int().positive(),
    leadId: z.string().nullable(), leadUrl: z.string().nullable(), customerLabel: z.string().nullable(),
    origin: z.enum(["leader", "manager", "yulia", "miniapp", "yana"]), createdBy: z.string().uuid().nullable(),
    attendance: z.enum(["unknown", "held", "client_no_show"]).default("unknown"),
    confirmation: z.object({
        status: z.enum(["not_required", "waiting", "confirmed"]),
        confirmedAt: z.string().datetime().nullable(),
    }).strict().optional(),
    ownerExceptionEligible: z.boolean().optional(),
    noShowRescheduleCount: z.number().int().min(0).max(1).default(0),
    sync: z.object({ state: z.enum(["pending", "leased", "complete", "ambiguous", "blocked", "no_channel"]), message: z.string().nullable() }).optional(),
    integrationMode: NativeCalendarIntegrationModeSchema,
}).strict();
/** «Куда пишем»: the person's channels for one booking and the one the memo and reminders use. */
export const NativeCalendarBookingChannelsSchema = z.object({
    bookingId: z.string().uuid(),
    bookingVersion: z.number().int().positive(),
    channels: z.array(ContactChannelSchema).max(40),
    staffChoice: z.object({
        channelKey: z.string().min(5).max(300),
        chosenAt: z.string().datetime(),
        droppedAt: z.string().datetime().nullable(),
        droppedReason: z.string().nullable(),
    }).strict().nullable(),
    noChannelAt: z.string().datetime().nullable(),
    // No working channel yet, but the calendar keeps looking until this moment
    // (ten minutes after the memo); the team is alerted only after it.
    channelSearchUntil: z.string().datetime().nullable().optional(),
    // A memo or reminder that did not go 15 minutes after its time (306): it
    // will not go by itself; the team and the consultant were told.
    notSent: z.array(z.object({
        kind: z.enum(["memo", "reminder"]),
        dueAt: z.string().datetime(),
        reason: z.string().min(1).max(1_000),
    }).strict()).max(20).optional(),
    canChoose: z.boolean(),
}).strict();
/** A staff choice for one booking version; null returns to the shared rule. */
export const NativeCalendarChannelChoiceSchema = z.object({
    expectedVersion: z.number().int().positive(),
    channelKey: z.string().min(5).max(300).nullable(),
}).strict();
// An immutable old appointment, not a second active booking. The decision is
// anchored to when it was changed; opening the calendar later cannot change it.
export const NATIVE_CALENDAR_LATE_CHANGE_MINUTES = 60;
export const NativeCalendarTraceSchema = z.object({
    id: z.string().uuid(), kind: z.enum(["cancelled", "rescheduled"]),
    changedAt: z.string().datetime(), booking: NativeCalendarBookingSchema,
}).strict();
export const NativeCalendarSlotSchema = z.object({
    managerId: z.string().uuid(), date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema,
    state: z.enum(["closed", "free", "booked", "blocked", "day_off", "vacation"]), bookingId: z.string().uuid().nullable(),
}).strict();
export const NativeCalendarAbsenceSchema = z.object({
    id: z.string().uuid(), managerId: z.string().uuid(), from: NativeCalendarDateSchema, to: NativeCalendarDateSchema,
    kind: z.enum(["day_off", "vacation"]), version: z.number().int().positive(),
}).strict();
export const NativeCalendarBlockSchema = z.object({
    id: z.string().uuid(), managerIds: z.array(z.string().uuid()).min(1).max(100), date: NativeCalendarDateSchema,
    startHour: NativeCalendarHourSchema, endHour: z.number().int().min(7).max(23), title: z.string().min(1).max(200),
    version: z.number().int().positive(),
}).strict();
// The descriptions reach the owner's calendar tool schema; validation is unchanged.
// Weekly rules are evaluated in Moscow local dates, never materialized into finite rows.
export const NativeCalendarRecurringBlockSchema = z.object({
    id: z.string().uuid(), weekday: z.number().int().min(1).max(7),
    from: NativeCalendarDateSchema, timezone: z.literal("Europe/Moscow"),
    startHour: NativeCalendarHourSchema, endHour: z.number().int().min(7).max(23),
    title: z.string().trim().min(1).max(200), version: z.number().int().positive(),
}).strict();
export const NativeCalendarRecurringBlockMutationSchema = NativeCalendarRecurringBlockSchema.omit({ id: true, version: true }).extend({
    idempotencyKey: NativeCalendarIdempotencyKeySchema,
}).refine(b => b.endHour > b.startHour, "Окончание должно быть позже начала.");
export function recurringCalendarBlockMatches(rule, date, hour) {
    return date >= rule.from && ((new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7 + 1) === rule.weekday
        && hour >= rule.startHour && hour < rule.endHour;
}
export const NativeCalendarPhraseSchema = z.object({
    text: z.string().trim().min(1).max(200).describe("Кодовая фраза точно так, как её пишут люди."),
    purpose: z.enum(["application", "bonus", "offer", "follow_up"]).describe("Назначение фразы: application — заявка, bonus — бонус, offer — оффер, follow_up — дожим (посты и сообщения после эфира). "
        + "В серии эфира дожим — отдельное событие серии, все фразы которого follow_up; у одиночного эфира без серии — фраза follow_up в самом эфире."),
    validFrom: NativeCalendarDateSchema.describe("Первый день действия фразы (YYYY-MM-DD, Москва). У дожима — день первого поста."),
    validTo: NativeCalendarDateSchema.nullable().describe("Последний день действия фразы включительно. Если Паша не сказал иначе — 15-е число следующего месяца после последнего дня серии "
        + "(у одиночного эфира — после дня эфира), и для дней, и для дожима. null — только если Паша прямо сказал «бессрочно»."),
    qualification: z.enum(["none", "circle"]).optional().describe("Квалификация перед записью по этому слову: circle — сначала кружочек Дмитрия в мини-приложении (кнопка «Выбрать время» откроется после "
        + "его обращения; кто пишет в чат — голосовое Дмитрия от Яны), none — запись сразу. Не указано — как было у эфира до 20.10.2026."),
}).strict().refine((p) => p.validTo === null || p.validTo >= p.validFrom, "Неверный срок действия фразы.");
/**
 * Owner rule 30.09.2026: code phrases work until the 15th of the month after the
 * last day of the series (broadcasts are late in a month; people write early in the
 * next one, not in its middle). Applies to the days' phrases and the follow-up.
 */
export function defaultCodePhraseValidTo(lastDay) {
    const [year, month] = NativeCalendarDateSchema.parse(lastDay).split("-").map(Number);
    return month === 12 ? `${year + 1}-01-15` : `${year}-${String(month + 1).padStart(2, "0")}-15`;
}
/**
 * After a phrase ends, customer Yana still recognizes it for this many days as a
 * past broadcast's phrase: with the default end on the 15th, that covers a late
 * reply to a recording until roughly three months after the broadcast, while at
 * one broadcast series a month the list stays at two or three past series.
 */
export const EXPIRED_CODE_PHRASE_RECOGNITION_DAYS = 60;
/** A broadcast series: its days and its follow-up (дожим) are events sharing one key. */
export const NativeCalendarSeriesKeySchema = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,119}$/u, "Ключ серии: латиница, цифры, точка, дефис.")
    .describe("Постоянный ключ серии, одинаковый у всех её дней и дожима, например live-2026-09-22.");
export const NativeCalendarEventSeriesSchema = z.object({
    key: NativeCalendarSeriesKeySchema,
    title: z.string().trim().min(1).max(200).describe("Название серии, например «Интенсив «Создатель перемен», 22–24.09»."),
}).strict().describe("Серия эфира: дни (День 1, День 2, День 3 или бонусный) и дожим после всех дней. Дожим серии — отдельное событие этой серии, все фразы которого follow_up.");
/** A series follow-up (дожим) event: it has phrases and every one is follow_up. */
export function isNativeCalendarFollowUpEvent(event) {
    return event.phrases.length > 0 && event.phrases.every((phrase) => phrase.purpose === "follow_up");
}
const EventFields = {
    title: z.string().trim().min(1).max(200), date: NativeCalendarDateSchema,
    time: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u).nullable().default(null), campaignKey: z.string().trim().min(1).max(160),
    demandDates: z.array(NativeCalendarDateSchema).max(62), phrases: z.array(NativeCalendarPhraseSchema).max(40),
    description: z.string().max(4000),
};
export const NativeCalendarEventSchema = z.object({ id: z.string().uuid(), ...EventFields, series: NativeCalendarEventSeriesSchema.nullable().default(null), qualificationRequired: z.boolean(), version: z.number().int().positive() }).strict();
export const NativeCalendarSeriesListSchema = z.object({ series: z.array(z.object({
        key: NativeCalendarSeriesKeySchema, title: z.string(),
        events: z.array(z.object({ id: z.string().uuid(), title: z.string(), date: NativeCalendarDateSchema, followUp: z.boolean() }).strict()),
    }).strict()) }).strict();
export const NativeCalendarNotificationSchema = z.object({
    id: z.string().uuid(), kind: z.string(), bookingId: z.string().uuid().nullable(), text: z.string(),
    createdAt: z.string().datetime(), state: NativeCalendarIntegrationModeSchema,
}).strict();
export const NativeCalendarSnapshotSchema = z.object({
    from: NativeCalendarDateSchema, to: NativeCalendarDateSchema, managers: z.array(NativeCalendarManagerSchema),
    slots: z.array(NativeCalendarSlotSchema), bookings: z.array(NativeCalendarBookingSchema),
    traces: z.array(NativeCalendarTraceSchema).default([]),
    recurringBlocks: z.array(NativeCalendarRecurringBlockSchema).default([]),
    absences: z.array(NativeCalendarAbsenceSchema), blocks: z.array(NativeCalendarBlockSchema),
    events: z.array(NativeCalendarEventSchema), notifications: z.array(NativeCalendarNotificationSchema),
}).strict();
const MutationFields = { idempotencyKey: NativeCalendarIdempotencyKeySchema };
export const NativeCalendarSlotsMutationSchema = z.object({
    ...MutationFields, managerId: z.string().uuid(), date: NativeCalendarDateSchema,
    hours: z.array(NativeCalendarHourSchema).min(1).max(17).refine((h) => new Set(h).size === h.length),
    operation: z.enum(["open", "close"]),
}).strict();
export const NativeCalendarAbsenceMutationSchema = z.object({
    ...MutationFields, managerId: z.string().uuid(), from: NativeCalendarDateSchema, to: NativeCalendarDateSchema,
    kind: z.enum(["day_off", "vacation"]),
}).strict().refine(({ from, to }) => to >= from && Date.parse(to) - Date.parse(from) <= 365 * 86_400_000, "Неверный период отсутствия.");
export const NativeCalendarBlockMutationSchema = z.object({
    ...MutationFields, managerIds: z.array(z.string().uuid()).min(1).max(100).refine((ids) => new Set(ids).size === ids.length),
    date: NativeCalendarDateSchema, startHour: NativeCalendarHourSchema, endHour: z.number().int().min(7).max(23),
    title: z.string().trim().min(1).max(200),
}).strict().refine((b) => b.endHour > b.startHour, "Окончание должно быть позже начала.");
export const NativeCalendarVersionMutationSchema = z.object({ ...MutationFields, expectedVersion: z.number().int().positive() }).strict();
export const NativeCalendarLeadResolveSchema = z.object({ leadUrl: z.string().trim().min(1).max(500) }).strict();
export const NativeCalendarLeadResolutionSchema = z.object({
    leadId: z.string(), leadUrl: z.string(), customerLabel: z.null(), lookupStatus: z.literal("not_fetched"),
}).strict();
export const NativeCalendarLeadResultSchema = z.discriminatedUnion("lookupStatus", [
    NativeCalendarLeadResolutionSchema,
    z.object({ leadId: z.string(), leadUrl: z.string(), customerLabel: z.string().min(1).max(200), lookupStatus: z.literal("found") }).strict(),
]);
export const NativeCalendarBookingMutationSchema = z.object({
    ...MutationFields, managerId: z.string().uuid(), date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema,
    leadUrl: z.string().trim().min(1).max(500), customerLabel: z.string().trim().min(1).max(200).optional(),
}).strict();
export const NativeCalendarRescheduleMutationSchema = z.object({
    ...MutationFields, managerId: z.string().uuid(), date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema,
    expectedVersion: z.number().int().positive(),
}).strict();
// Omitted on create means no qualification; omitted on update keeps the stored choice.
// The series follows the same rule: omitted on update keeps it, null leaves the series.
export const NativeCalendarEventMutationSchema = z.object({ ...MutationFields, ...EventFields, series: NativeCalendarEventSeriesSchema.nullable().optional(), qualificationRequired: z.boolean().optional() }).strict();
export const NativeCalendarEventUpdateSchema = NativeCalendarEventMutationSchema.extend({ expectedVersion: z.number().int().positive() });
export const NativeCalendarAccessGroupSchema = z.object({
    id: z.string().uuid(), name: z.string().trim().min(2).max(100), role: NativeCalendarRoleSchema,
    operatorAccess: z.boolean(), version: z.number().int().positive(),
}).strict();
export const NativeCalendarAccessGroupsSchema = z.object({ groups: z.array(NativeCalendarAccessGroupSchema) }).strict();
export const NativeCalendarAccessGroupMutationSchema = NativeCalendarAccessGroupSchema.omit({ id: true, version: true }).extend({
    ...MutationFields, id: z.string().uuid().optional(), expectedVersion: z.number().int().positive().optional(),
}).strict();
export const NativeCalendarProfileMutationSchema = z.object({
    ...MutationFields, groupId: z.string().uuid().nullable().optional(), userId: z.string().uuid(), role: NativeCalendarRoleSchema, managerId: z.string().uuid().nullable(), operatorAccess: z.boolean(),
}).strict().refine((p) => p.role !== "manager" || p.managerId !== null, "Менеджеру требуется профиль консультанта.");
export const NativeCalendarProfilesSchema = z.object({ profiles: z.array(z.object({
        groupId: z.string().uuid().nullable().optional(), userId: z.string().uuid(), name: z.string(), role: NativeCalendarRoleSchema,
        managerId: z.string().uuid().nullable(), operatorAccess: z.boolean(),
    }).strict()) }).strict();
export const NativeCalendarMutationResultSchema = z.object({ id: z.string().uuid(), version: z.number().int().positive(), replayed: z.boolean() }).strict();
export const NativeCalendarBookingResultSchema = z.object({ booking: NativeCalendarBookingSchema, replayed: z.boolean() }).strict();
export const NativeCalendarCustomerSchema = z.object({
    telegramUserId: z.string().regex(/^[1-9]\d{0,19}$/u), practicumUserId: z.string().min(1).max(160).optional(),
    acquisitionRegistrationId: z.string().uuid().optional(),
    journeyId: z.string().min(1).max(200).nullable(), crmLeadUrl: z.string().min(1).max(500).optional(),
    displayName: z.string().trim().min(1).max(200).optional(),
}).strict().refine(value => Boolean(value.practicumUserId) !== Boolean(value.acquisitionRegistrationId), "Нужен один источник регистрации клиента.");
export const NativeCalendarCustomerStateInputSchema = z.object({ customer: NativeCalendarCustomerSchema }).strict();
export const NativeCalendarCustomerIntentSchema = z.object({ customer: NativeCalendarCustomerSchema, openedAt: z.string().datetime() }).strict();
/**
 * The Mini App reports that this Telegram person watched Dmitry's qualification
 * video («кружочек») at least up to the payment sentence. The Mini App owns the
 * threshold of each video version; VEHA records the first watch and Yana then
 * skips Dmitry's audio and the qualification step (08.10.2026).
 */
export const NativeCalendarQualificationVideoVersionSchema = z.string().regex(/^v\d{2}$/u);
export const NativeCalendarQualificationVideoInputSchema = z.object({
    customer: NativeCalendarCustomerSchema,
    version: NativeCalendarQualificationVideoVersionSchema,
    seconds: z.number().int().min(0).max(600),
    watchedAt: z.string().datetime(),
}).strict();
export const NativeCalendarQualificationVideoResultSchema = z.object({ recorded: z.literal(true), watchedAt: z.string().datetime() }).strict();
/**
 * Signed Mini App report (Ден 28.10.2026): the result of the quiz of a site form registration of this person, passed or
 * not — every quiz registration, so the latest one decides and an older pass never outlives a newer failed quiz.
 */
export const NativeCalendarQualificationQuizInputSchema = z.object({
    customer: NativeCalendarCustomerSchema,
    registrationId: z.string().uuid(),
    passed: z.boolean(),
    score: z.number().int().min(0).max(1000),
    submittedAt: z.string().datetime(),
}).strict();
export const NativeCalendarQualificationQuizResultSchema = z.object({ recorded: z.literal(true), passed: z.boolean(), submittedAt: z.string().datetime() }).strict();
/** Fresh CRM evidence is supplied only by the trusted server after an exact identity read. */
export const NativeCalendarAbandonmentInputSchema = z.object({
    customer: NativeCalendarCustomerSchema,
    openedAt: z.string().datetime(),
    crmEvidence: z.object({
        contactId: z.string().regex(/^[1-9]\d{0,18}$/u),
        leadIds: z.array(z.string().regex(/^[1-9]\d{0,18}$/u)).max(100),
        phoneKnown: z.boolean(), readAt: z.string().datetime(),
    }).strict(),
}).strict();
export const NativeCalendarAbandonmentResultSchema = z.object({
    decision: z.enum(["eligible", "suppress", "defer"]),
    reason: z.enum(["ELIGIBLE", "NOT_DUE", "CRM_EVIDENCE_STALE", "CALENDAR_NOT_READY", "ACTIVE_BOOKING",
        "CRM_HANDLED", "EXISTING_ACTIVITY", "CHANNEL_NOT_READY"]),
    checkedAt: z.string().datetime(), evidenceComplete: z.boolean(),
}).strict();
export const NativeCalendarCustomerAvailabilityInputSchema = z.object({ customer: NativeCalendarCustomerSchema, from: NativeCalendarDateSchema, to: NativeCalendarDateSchema }).strict().refine(({ from, to }) => NativeCalendarRangeSchema.safeParse({ from, to }).success, "Неверный период.");
export const NativeCalendarCustomerBookSchema = z.object({
    ...MutationFields, customer: NativeCalendarCustomerSchema, date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema,
    disclosureVersion: z.string().min(1).max(160),
    dedupLeadUrl: z.string().min(1).max(500).optional(),
}).strict();
/** Exact CRM identity asserted by the trusted Practicum reconciler after booking. */
export const NativeCalendarCustomerLinkSchema = z.object({
    ...MutationFields,
    customer: NativeCalendarCustomerSchema.safeExtend({ crmLeadUrl: z.string().min(1).max(500) }),
    bookingId: z.string().uuid(),
}).strict();
export const NativeCalendarCustomerRescheduleSchema = z.object({
    ...MutationFields, customer: NativeCalendarCustomerSchema, bookingId: z.string().uuid(),
    date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema, expectedVersion: z.number().int().positive(),
}).strict();
export const NativeCalendarCustomerCancelSchema = z.object({
    ...MutationFields, customer: NativeCalendarCustomerSchema, bookingId: z.string().uuid(), expectedVersion: z.number().int().positive(),
}).strict();
export const NativeCalendarCustomerBookingSchema = NativeCalendarBookingSchema.pick({ id: true, date: true, hour: true, state: true, version: true, leadId: true, attendance: true, integrationMode: true }).extend({ timezone: z.literal("Europe/Moscow"), origin: NativeCalendarBookingSchema.shape.origin.optional(), journeyId: z.string().nullable().optional(), createdAt: z.string().datetime().optional(), acquisitionRegistrationId: z.string().uuid().nullable().optional() });
export const NativeCalendarCustomerBookingResultSchema = z.object({ booking: NativeCalendarCustomerBookingSchema, replayed: z.boolean() }).strict();
export const NativeCalendarCustomerAvailabilitySchema = z.object({
    timezone: z.literal("Europe/Moscow"), minimumLeadMinutes: z.literal(180),
    slots: z.array(z.object({ date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema }).strict()), integrationMode: NativeCalendarIntegrationModeSchema,
}).strict();
export const NativeCalendarEligibilitySchema = z.object({
    decision: z.enum(["allowed", "care_required", "review_required"]),
    reason: z.enum(["first_booking", "first_no_show_repeat", "owner_exception", "active_booking", "repeat_no_show", "consultation_held", "outcome_unknown", "staff_followup", "late_change", "identity_unresolved", "already_student"]),
    canBook: z.boolean(), canCancel: z.boolean(), canReschedule: z.boolean(),
    noShowCount: z.number().int().nonnegative(), minimumChangeLeadMinutes: z.literal(180),
}).strict();
export const NativeCalendarOwnerExceptionMutationSchema = NativeCalendarVersionMutationSchema.extend({ evidence: z.string().trim().min(3).max(2000) }).strict();
export const NativeCalendarAttendanceMutationSchema = NativeCalendarVersionMutationSchema.extend({
    outcome: z.enum(["held", "client_no_show"]), evidence: z.string().trim().min(3).max(2000),
}).strict();
export const NativeCalendarCustomerChangeFeedInputSchema = z.object({ afterCursor: z.string().regex(/^\d{1,19}$/u).nullable().optional(), limit: z.number().int().min(1).max(100).default(100) }).strict();
export const NativeCalendarCustomerChangeFeedSchema = z.object({
    changes: z.array(z.object({ bookingId: z.string().uuid(), bookingVersion: z.number().int().positive(), kind: z.enum(["rescheduled", "cancelled"]), date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema, occurredAt: z.string().datetime(), telegramUserId: z.string().nullable(), practicumUserId: z.string().nullable(), acquisitionRegistrationId: z.string().uuid().nullable().optional() }).strict()),
    nextCursor: z.string().nullable(), hasMore: z.boolean(), integrationMode: NativeCalendarIntegrationModeSchema,
}).strict();
export const NativeCalendarCustomerChangesInputSchema = z.object({ customer: NativeCalendarCustomerSchema }).strict();
export const NativeCalendarCustomerChangesSchema = z.object({
    changes: z.array(z.object({ bookingId: z.string().uuid(), bookingVersion: z.number().int().positive(), kind: z.enum(["rescheduled", "cancelled"]), date: NativeCalendarDateSchema, hour: NativeCalendarHourSchema, occurredAt: z.string().datetime() }).strict()),
    integrationMode: NativeCalendarIntegrationModeSchema,
}).strict();
export const NativeCalendarCustomerStateSchema = z.object({
    eligibility: NativeCalendarEligibilitySchema,
    booking: NativeCalendarCustomerBookingSchema.nullable(),
    suppression: z.object({ hasBooking: z.boolean(), managerHandled: z.literal(false), refused: z.literal(false), cancelled: z.boolean(), identityLinked: z.boolean() }).strict(),
    integrationMode: NativeCalendarIntegrationModeSchema,
}).strict();
/** Identical canonical bytes for server/BFF signing; excludes unsupported JSON values. */
export function canonicalCalendarJson(value) {
    if (value === null || typeof value === "string" || typeof value === "boolean" || (typeof value === "number" && Number.isFinite(value)))
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map(canonicalCalendarJson).join(",")}]`;
    if (typeof value === "object" && value !== null) {
        return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonicalCalendarJson(Reflect.get(value, key))}`).join(",")}}`;
    }
    throw new TypeError("Calendar request must be JSON.");
}
//# sourceMappingURL=native-calendar.js.map